/**
 * Indexable seed story pages (2026-09-30): `/<lang>/<section>/<slug>` and the index `/<lang>/<section>/`.
 * The backend renders them for crawlers (backend/seo.py); the SPA boots over that HTML into SeoStoryPage /
 * SeoIndexPage. This table = `backend/seo.py SECTIONS` (parity test backend/tests/test_seo_pages.py) = the
 * language list of the Apache rule in configs/050-snugtale.conf: change all three together.
 */
export const SEO_SECTIONS: Record<string, string> = {
  "de": "gute-nacht-geschichten",
  "en": "bedtime-stories",
  "fr": "histoires-du-soir",
  "es": "cuentos-para-dormir",
  "it": "favole-della-buonanotte",
  "pt": "historias-para-dormir",
  "nl": "verhaaltjes-voor-het-slapengaan",
  "pl": "bajki-na-dobranoc",
  "sv": "godnattsagor",
  "cs": "pohadky-na-dobrou-noc",
  "tr": "uyku-masallari",
  "uk": "kazky-na-nich",
  "ja": "nemuru-mae-no-ohanashi",
  "ko": "jamjari-donghwa",
};

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Is `/<lang>/<section>` one of ours? (The SPA route is generic; anything else falls through to the app home.) */
export function isSeoSection(lang: string | undefined, section: string | undefined): boolean {
  return !!lang && !!section && SEO_SECTIONS[lang] === section;
}

export function isSeoSlug(slug: string | undefined): boolean {
  return !!slug && SLUG_RE.test(slug);
}

export function seoIndexPath(lang: string): string {
  return `/${lang}/${SEO_SECTIONS[lang]}/`;
}

/**
 * The path the server rendered as a search page at boot (its #root carried `data-seo-ssr` before React replaced
 * it), else null. Captured when App.tsx imports this module — before the first render. A search page opened with
 * this path was reached from outside (Google, a shared link): its landing is `seo-<lang>`. The same page reached
 * inside the app (a library card) is `lib-<lang>`, so search traffic reads apart from library browsing.
 */
export const SSR_BOOT_PATH: string | null =
  typeof document !== 'undefined' && document.querySelector('#root [data-seo-ssr]') ? window.location.pathname : null;

/** Arrived on a search page from outside at some point in this tab: the whole visit stays `seo-<lang>` (a Google
 *  visitor who follows a neighbour link is still search traffic). Library-only browsing stays `lib-<lang>`. */
const ARRIVAL_KEY = 'snugtale_seo_arrival_v1';
if (SSR_BOOT_PATH) {
  try { sessionStorage.setItem(ARRIVAL_KEY, '1'); } catch { /* storage unavailable */ }
}
export function arrivedFromSearch(): boolean {
  if (SSR_BOOT_PATH) return true;
  try { return sessionStorage.getItem(ARRIVAL_KEY) === '1'; } catch { return false; }
}
